import type { Locale } from '../../../../types';
import type { ScopedUtils } from '../../shared/types';
import { buildDate } from './buildDate';
import type { DateTimeFormatFunction, DateTimeSettings, DateTimeFormatterInstancesMap } from './types';
import { dateTimeFormatterFactories } from './dateTimeFormatterFactories';
import { dateTimePostProcessors } from './dateTimePostProcessors';
import { createPostProcessor } from '../../shared/post-processors';
import { createFriendlyUtils } from './createFriendlyUtils';


export const createDateTimeFormatter = (
    locale: Locale,
    settings: DateTimeSettings,
    scopedUtils: ScopedUtils,
): DateTimeFormatFunction => {
    const formatters: DateTimeFormatterInstancesMap = Object.create(null);

    Object.entries(dateTimeFormatterFactories).forEach(([key, factory]) => {
        formatters[key as keyof typeof dateTimeFormatterFactories] = factory(locale, settings, formatters, scopedUtils);
    });
    
    const { getFriendlyFormatter } = createFriendlyUtils(locale, settings, formatters);
    const postProcessorsPipe = createPostProcessor(dateTimePostProcessors, locale);

    return (
        dateToFormat,
        format,
        options
    ) => {
        let formatter = formatters[format];

        if (!formatter) {
            throw new Error(`DateTime format not found: ${format}. Please configure DateTime format for ${format}.`);
        }

        // regular format processing
        const date = buildDate(dateToFormat, settings);

        if (!!options?.friendly) {
            formatter = getFriendlyFormatter(date, format);
        }

        return postProcessorsPipe(formatter.format(date), format, settings);
    };
}
